import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1557264322-b44d383a2906?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden pt-28" data-testid="hero">
      {/* parallax cinematic backdrop — still image base (instant); light video only on desktop */}
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <div
          className="h-[120%] w-full bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        {isDesktop && (
          <video
            autoPlay loop muted playsInline preload="auto"
            className="absolute inset-0 h-[120%] w-full object-cover opacity-40"
          >
            <source src="https://videos.pexels.com/video-files/2098988/2098988-sd_640_360_30fps.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/45 to-[#050505]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-6 pt-16 sm:pt-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
          <span className="text-xs font-medium uppercase tracking-[0.25em] text-white/60">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-anton text-white leading-[0.82] tracking-tightest">
          {[t.hero.l1, t.hero.l2, t.hero.l3].map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block text-[15vw] sm:text-[13vw] lg:text-[11rem]"
                variants={line}
                initial="hidden"
                animate="show"
                custom={i}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
        >
          <p className="max-w-md text-base text-white/70 leading-relaxed">
            {t.hero.desc}
          </p>
          <div className="flex items-center gap-2 text-white/50">
            <ArrowDownRight className="h-5 w-5 animate-bounce" />
            <span className="text-xs uppercase tracking-[0.2em]">{t.hero.scroll}</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
